import { FormEvent, useEffect, useState } from "react";
import { postReq } from "../../utils/request";

import {  useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { getJwtToken, setJwtToken } from "../../utils/token";
import { useUserPermissions } from "../../contexts/UserPermissionsContext";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import Input from "../../components/Input";
import auth from "./auth.module.scss";

const VerifyLoginForm = () => {
  const [verificationCode, setVerificationCode] = useState("");
  const navigate = useNavigate();
  const { setCurrentUser } = useUserPermissions();
  const [verificationFailed, setVerificationFailed] = useState("");
  
  const [countdown, setCountdown] = useState({ minutes: 0, seconds: 0 });;

  useEffect(() => {
    const token = getJwtToken();
    if (!token) {
      return;
    }
    const decodedToken = jwtDecode(token);

    // Derive the countdown from the token's OWN lifetime (exp - iat). Both are
    // server-issued timestamps, so their difference is immune to device-clock
    // skew. Comparing exp against the device clock (new Date()) used to eject
    // users whose device clock ran ahead of the server by more than the token's
    // lifetime: a valid, freshly-issued token looked "already expired" on that
    // one device and bounced them straight back to /login while everyone else
    // was fine. The server remains the real authority — it rejects a genuinely
    // expired code on submit with "Token expired, login again".
    let lifetimeSeconds = 0;
    if (decodedToken.exp && decodedToken.iat) {
      lifetimeSeconds = decodedToken.exp - decodedToken.iat;
    } else if (decodedToken.exp) {
      // No iat to measure against — fall back to the device clock, but floor at
      // zero so a skewed clock can never turn this into a redirect.
      lifetimeSeconds = Math.max(0, Math.floor((decodedToken.exp * 1000 - Date.now()) / 1000));
    }

    // No trustworthy lifetime (missing/expired claims) — don't eject; just show
    // the form and let the server validate the code on submit.
    if (lifetimeSeconds <= 0) {
      return;
    }

    // Anchor an absolute deadline on the DEVICE clock (now + lifetime), then
    // re-derive the remaining time from it on every tick. Decrementing once
    // per tick drifted badly in background tabs, where browsers throttle
    // setInterval to as little as one tick per minute — the display fell
    // behind real time exactly while the user was off reading the code in
    // their inbox. Anchor and readings use the same device clock, so the
    // clock-skew immunity described above is preserved.
    //
    // The deadline is persisted per-token in sessionStorage so a page refresh
    // RESUMES the countdown instead of restarting it at the full lifetime; a
    // fresh login issues a new token, which starts a new deadline.
    const DEADLINE_KEY = "verify2faDeadline";
    let deadlineMs = Date.now() + lifetimeSeconds * 1000;
    try {
      const saved = JSON.parse(sessionStorage.getItem(DEADLINE_KEY) || "null");
      if (saved && saved.token === token && typeof saved.deadlineMs === "number") {
        deadlineMs = saved.deadlineMs;
      } else {
        sessionStorage.setItem(DEADLINE_KEY, JSON.stringify({ token, deadlineMs }));
      }
    } catch {
      // Storage unavailable — fall back to the in-memory anchor.
    }
    const remainingParts = () => {
      const secondsLeft = Math.max(0, Math.round((deadlineMs - Date.now()) / 1000));
      return { minutes: Math.floor(secondsLeft / 60), seconds: secondsLeft % 60 };
    };

    setCountdown(remainingParts());
    let ejected = false;
    const tick = () => {
      const parts = remainingParts();
      setCountdown(parts);
      if (parts.minutes === 0 && parts.seconds === 0 && !ejected) {
        ejected = true;
        clearInterval(intervalId);
        try { sessionStorage.removeItem(DEADLINE_KEY); } catch { /* ignore */ }
        setJwtToken("");
        Cookies.remove('refreshToken');
        navigate("/login");
      }
    };
    const intervalId = setInterval(tick, 1000);
    // Snap to the true remaining time the instant the tab is foregrounded.
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [navigate]);

  const onVerificationFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!verificationCode) {
        setVerificationFailed("Enter verification code");
      return;
    }
    const resp = await postReq("users/2fa", {
      verificationCode,
    });
    if (resp.status === 200) {
      try { sessionStorage.removeItem("verify2faDeadline"); } catch { /* ignore */ }
      setJwtToken(resp.body.accessToken);
      setCurrentUser(resp.body.user);
      navigate("/dashboard");
    } else {
      // Verification failed
      if(resp.status === 500)
      setVerificationFailed("Unexpected error, please contact admin");
      if(resp.status === 401)
      {
        setVerificationFailed(resp.body.message);
      }
      if(resp.status === 400)
      {
        setVerificationFailed(resp.body.message);
      }
      if(resp.status === 403)
        {
          setVerificationFailed(resp.body.message);
          toast.error(resp.body.message, {
            autoClose: 5000, // 5 seconds
            className: "toast-success-message",
            onClose: () => navigate("/login"),
          });
        }
    }
  };

  return (
    <>
      <div className={`container`}>
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="d-flex justify-content-between align-items-center">
              <span className="componentTitle">
                Enter code sent to your email
              </span>
              <span className="componentTitle">
              {countdown.minutes}:{countdown.seconds < 10 ? `0${countdown.seconds}` : countdown.seconds}
              </span>
            </div>
            <form
              className={`verification-form ${auth.authForm}`}
              onSubmit={onVerificationFormSubmit}
            >
              <div className="row">
                <div className={auth.authField}>
                    <Input
                      type="text"
                      id="verification_code"
                      label="Verification code"
                      className={auth.codeField}
                      value={verificationCode}
                      onChange={(_, value) => setVerificationCode(String(value))}
                      nativeProps={{
                        name: "verificationCode",
                        autoFocus: true,
                        inputMode: "numeric",
                        autoComplete: "one-time-code",
                        enterKeyHint: "go",
                        maxLength: 6,
                      }}
                    />
                    {verificationFailed ? (
                        <span className="errorClass">{verificationFailed}</span>
                      ) : null}
                  </div>
                <div className={auth.authActions}>
                  <Button type="submit" id="verify" variant="primary" size="lg" title="Check the code and sign in">
                    Verify code
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
export default VerifyLoginForm;
